import { PersonCustomFieldFields } from 'src/app/people/models/peopleFilterFields';
import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import {
  FilterGroup,
  ODataFilter,
  ODataFilterValues,
  ODataSubFilter,
} from '../components/odata-filter/models/odata-filter';
import { FilterOperator, OPERATORS } from '../components/odata-filter/models/operators';
import { SearchValueType } from '../components/odata-filter/models/search-value-type';
import {
  endOfLastWeek,
  endOfWeek,
  getDate,
  startOfLastWeek,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  startOfLastMonth,
  endOfLastMonth,
  yesterday,
  thisYear,
  lastYear,
  today,
} from '../utils/date-utils';
import { ODataFilterBuilder } from './odata-filter-builder';

export class CustomFieldFilterModel implements ODataFilter {
  childFieldName = '';
  subFilter = new ODataSubFilter();
  constructor(
    public filterGroup: FilterGroup,
    public displayName: string,
    public targetField: string,
    public targetFieldId: string,
    public valueType: SearchValueType,
    public values?: any[] | ODataFilterValues[]
  ) {}
  buildFilter(
    searchValue: any,
    filterOperator: FilterOperator,
    subQueryFilters?: any[],
    buildForAutomation?: boolean
  ) {
    if (
      this.valueType == SearchValueType.String ||
      this.valueType == SearchValueType.EmptyOperator
    ) {
      return this.buildStringTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.Number) {
      return this.buildNumberTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.MultiValue) {
      return this.buildEnumeratedTypeFilter(searchValue, filterOperator);
    } else if (this.valueType == SearchValueType.Date) {
      return this.buildDateTypeFilter(searchValue, filterOperator, buildForAutomation);
    } else if (this.valueType == SearchValueType.DateRange) {
      return this.buildDateRangeTypeFilter(searchValue, filterOperator, buildForAutomation);
    } else {
      throw new Error('Invalid Operation');
    }
  }

  private buildStringTypeFilter(searchValue: any, filterOperator: FilterOperator) {
    let operatorTypes = OPERATORS[this.valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    } else if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
          },
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
        },
      };
    } else {
      let filter = {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildStringFilter(
              'value',
              searchValue,
              filterOperator.actualOperator
            ),
          ],
        },
      };

      if (filterOperator.displayOperator == FilterOperatorType.notContains) return { not: filter };
      return filter;
    }
  }

  private buildNumberTypeFilter(searchValue: any, filterOperator: FilterOperator) {
    let operatorTypes = OPERATORS[this.valueType];

    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }
    if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
          },
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.notContains) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildNumberFilter(
              'to_double(value)',
              searchValue,
              FilterOperatorType.contains
            ),
          ],
        },
      };
    } else {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildNumberFilter(
              'to_double(value)',
              searchValue,
              filterOperator.actualOperator
            ),
          ],
        },
      };
    }
  }

  private buildEnumeratedTypeFilter(
    searchValue: ODataFilterValues[],
    filterOperator: FilterOperator
  ) {
    let _searchValue =
      searchValue && searchValue.length ? searchValue.map((x) => x.searchValue) : [];
    if (filterOperator.displayOperator == FilterOperatorType.in) {
      return {
        [this.targetField]: {
          any: [ODataFilterBuilder.buildArrayFilter('itemId', _searchValue)],
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.notIn) {
      return {
        not: {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildArrayFilter('itemId', _searchValue)],
          },
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
          },
        },
      };
    } else if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
        },
      };
    } else throw new Error('Invalid Operation');
  }

  buildDateTypeFilter(
    searchValue: any,
    filterOperator: FilterOperator,
    buildForAutomation: boolean
  ) {
    searchValue = getDate(searchValue);
    let operatorTypes = OPERATORS[this.valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    } else {
      if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
        return {
          not: {
            [this.targetField]: {
              any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
            },
          },
        };
      }

      if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
        return {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
          },
        };
      }
      if (
        filterOperator.displayOperator == FilterOperatorType.eq ||
        filterOperator.displayOperator == FilterOperatorType.lt ||
        filterOperator.displayOperator == FilterOperatorType.gt
      ) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter(
                'value',
                searchValue,
                filterOperator.displayOperator
              ),
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.today) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter('value', today(buildForAutomation)),
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.yesterday) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter('value', yesterday(buildForAutomation)),
            ],
          },
        };
      }
      if (
        filterOperator.displayOperator == FilterOperatorType.onOrBeforeToday ||
        filterOperator.displayOperator == FilterOperatorType.onOrAfterToday
      ) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter(
                'value',
                today(buildForAutomation),
                filterOperator.actualOperator
              ),
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.thisYear) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter(
                'value',
                thisYear(buildForAutomation),
                FilterOperatorType.startswith
              ),
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastYear) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              ODataFilterBuilder.buildSimpleFilter(
                'value',
                lastYear(buildForAutomation),
                FilterOperatorType.startswith
              ),
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.thisWeek) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              {
                and: [
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    startOfWeek(buildForAutomation),
                    FilterOperatorType.ge
                  ),
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    endOfWeek(buildForAutomation),
                    FilterOperatorType.le
                  ),
                ],
              },
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastWeek) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              {
                and: [
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    startOfLastWeek(buildForAutomation),
                    FilterOperatorType.ge
                  ),
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    endOfLastWeek(buildForAutomation),
                    FilterOperatorType.le
                  ),
                ],
              },
            ],
          },
        };
      }

      if (filterOperator.displayOperator == FilterOperatorType.thisMonth) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              {
                and: [
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    startOfMonth(buildForAutomation),
                    FilterOperatorType.ge
                  ),
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    endOfMonth(buildForAutomation),
                    FilterOperatorType.le
                  ),
                ],
              },
            ],
          },
        };
      }
      if (filterOperator.displayOperator == FilterOperatorType.lastMonth) {
        return {
          [this.targetField]: {
            any: [
              ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
              {
                and: [
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    startOfLastMonth(new Date(), buildForAutomation),
                    FilterOperatorType.ge
                  ),
                  ODataFilterBuilder.buildSimpleFilter(
                    'value',
                    endOfLastMonth(new Date(), buildForAutomation),
                    FilterOperatorType.le
                  ),
                ],
              },
            ],
          },
        };
      }
    }
  }

  buildDateRangeTypeFilter(
    searchValue: any,
    filterOperator: FilterOperator,
    buildForAutomation: boolean
  ) {
    searchValue = getDate(searchValue);
    let operatorTypes = OPERATORS[this.valueType];
    if (
      operatorTypes.operators.findIndex(
        (x) => x.displayOperator == filterOperator.displayOperator
      ) == -1
    ) {
      throw new Error('Invalid Operation');
    }
    if (filterOperator.displayOperator == FilterOperatorType.isEmpty) {
      return {
        not: {
          [this.targetField]: {
            any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
          },
        },
      };
    }

    if (filterOperator.displayOperator == FilterOperatorType.isNotEmpty) {
      return {
        [this.targetField]: {
          any: [ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId)],
        },
      };
    }

    if (
      filterOperator.displayOperator == FilterOperatorType.startsAfter ||
      filterOperator.displayOperator == FilterOperatorType.startsBefore
    ) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildDateFilter(
              PersonCustomFieldFields.dateRangeAttributeStartDateValue,
              searchValue,
              filterOperator.actualOperator,
              false,
              buildForAutomation
            ),
          ],
        },
      };
    }

    if (
      filterOperator.displayOperator == FilterOperatorType.endsAfter ||
      filterOperator.displayOperator == FilterOperatorType.endsBefore
    ) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildDateFilter(
              PersonCustomFieldFields.dateRangeAttributeEndDateValue,
              searchValue,
              filterOperator.actualOperator,
              false,
              buildForAutomation
            ),
          ],
        },
      };
    }

    if (
      filterOperator.displayOperator == FilterOperatorType.isCurrentlyActive ||
      filterOperator.displayOperator == FilterOperatorType.isNotCurrentlyActive ||
      filterOperator.displayOperator == FilterOperatorType.activeOn
    ) {
      return {
        [this.targetField]: {
          any: [
            ODataFilterBuilder.buildGuidFilter('id', this.targetFieldId),
            ODataFilterBuilder.buildDateRangeFilter(
              PersonCustomFieldFields.dateRangeAttributeStartDateValue,
              PersonCustomFieldFields.dateRangeAttributeEndDateValue,
              searchValue,
              filterOperator.actualOperator,
              buildForAutomation
            ),
          ],
        },
      };
    }
  }
}
